// store/thunks/blocksThunk.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";

export const createBlock = createAsyncThunk(
  "blocks/createBlock",
  async (blockData, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.post(
        `${baseURL}/api/blocks`,
        blockData,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const getBlock = createAsyncThunk(
  "blocks/getBlock",
  async (blockId, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(
        `${baseURL}/api/blocks/${blockId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const appendAppointmentToBlock = createAsyncThunk(
  "blocks/appendAppointmentToBlock",
  async ({ blockId, appointmentData }, { rejectWithValue, getState }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.post(
        `${baseURL}/api/blocks/${blockId}/appendAppointment`,
        appointmentData,
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addAppointmentToBlock = createAsyncThunk(
  "blocks/addAppointmentToBlock",
  async (
    { blockId, appointmentId, position },
    { rejectWithValue, getState }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.post(
        `${baseURL}/api/blocks/${blockId}/add-appointment`,
        { appointmentId, position },
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Creates a new block with an existing appointment
export const createBlockWithAppointment = createAsyncThunk(
  "blocks/createBlockWithAppointment",
  async (
    { appointmentId, blockData },
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });
    try {
      const response = await axios.post(
        `${baseURL}/api/blocks/create-with-appointment`,
        { appointmentId, blockData },
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const removeAppointmentFromBlock = createAsyncThunk(
  "blocks/removeAppointmentFromBlock",
  async ({ blockId, appointmentId }, { rejectWithValue, getState }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.post(
        `${baseURL}/api/blocks/${blockId}/remove-appointment`,
        { appointmentId },
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

export const fetchBlocksByClinicianId = createAsyncThunk(
  "blocks/fetchBlocksByClinicianId",
  async (clinicianId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/blocks/clinician/${clinicianId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchBlocksByClient = createAsyncThunk(
  "blocks/fetchBlocksByClient",
  async (clientId, { rejectWithValue, getState }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.get(
        `${baseURL}/api/blocks/client/${clientId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

export const linkBlocks = createAsyncThunk(
  "blocks/linkBlocks",
  async ({ blockAId, blockBId }, { rejectWithValue, getState }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.post(
        `${baseURL}/api/blocks/link`,
        { blockAId, blockBId },
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

export const unlinkBlock = createAsyncThunk(
  "blocks/unlinkBlock",
  async ({ blockAId, blockBId }, { rejectWithValue, getState }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.post(
        `${baseURL}/api/blocks/unlink`,
        { blockAId, blockBId },
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

export const updateBlock = createAsyncThunk(
  "blocks/updateBlock",
  async ({ blockId, updatedData }, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.patch(
        `${baseURL}/api/blocks/${blockId}`,
        updatedData,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const deleteBlock = createAsyncThunk(
  "blocks/deleteBlock",
  async (blockId, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const token = state.auth.token;
    const { baseURL, config } = generateConfig({ token });

    try {
      const response = await axios.delete(
        `${baseURL}/api/blocks/${blockId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);
